import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { ApiError, api, type AreaDetail, type AreaRow, type Cap, type Me } from "./lib/api";
import { startAutoFlush, listOutbox, onFlushEvent, type OutboxItem } from "./lib/outbox";
import { hasPin } from "./lib/pin";
import { ScreenLoading } from "./components";
import { Areas } from "./pages/Areas";
import { Dashboard } from "./pages/Dashboard";
import { Login } from "./pages/Login";
// The map (maplibre + terra-draw, ~370 KB gzipped) loads on demand: login,
// the list, the dashboard, and users never pay for it. Preloaded on idle
// after first paint so opening the peta view still feels instant (#41).
const MapPage = lazy(() => import("./pages/Map").then((m) => ({ default: m.MapPage })));
import { Timeline } from "./pages/Timeline";
import { Users } from "./pages/Users";
import { Database } from "./pages/Database";

export type View = "peta" | "dasbor" | "database" | "pengguna";

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [booting, setBooting] = useState(true);
  const [view, setView] = useState<View>("peta");
  // What is actually painted. Navigating to peta holds the previous screen
  // until the list and the map chunk are both in hand, so the user never
  // sees a half-painted view with a loader in it. Nav highlight follows
  // `view` (the tap registers at once); content follows `shownView`.
  const [shownView, setShownView] = useState<View>("peta");
  // null until the first fetch lands: the list must show loading, never a
  // "no areas" empty state that vanishes a second later (the phantom flash).
  const [areas, setAreas] = useState<AreaRow[] | null>(null);
  // The map chunk rides beside the data: peta paints once, when both have
  // landed, instead of popping header, then list, then map in stages.
  const [mapChunk, setMapChunk] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [detail, setDetail] = useState<AreaDetail | null>(null);
  const [outbox, setOutbox] = useState<OutboxItem[]>([]);
  const [uploading, setUploading] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [unlock, setUnlock] = useState(false);
  const [toast, setToast] = useState<{ tone: "red" | "green" | "blue"; text: string } | null>(null);

  const caps: Cap[] = me?.capabilities ?? [];
  const can = useCallback((c: Cap) => caps.includes(c), [caps]);

  const refreshAreas = useCallback(async () => {
    try {
      setAreas(await api.areas());
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setMe(null);
    }
  }, []);

  const refreshDetail = useCallback(async (id: number | null) => {
    if (id === null) {
      setDetail(null);
      return;
    }
    try {
      setDetail(await api.area(id));
    } catch {
      setDetail(null);
    }
  }, []);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const me0 = await api.me();
        if (!live) return;
        // The PIN lock guards a phone that is already signed in. It only appears
        // when there is a session to guard and a PIN has been set.
        setMe(me0);
        if (hasPin()) setUnlock(true);
      } catch {
        if (live) setMe(null);
      } finally {
        if (live) setBooting(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const refreshOutbox = useCallback(
    () => listOutbox().then(setOutbox).catch(() => setOutbox([])),
    [],
  );

  // Recording a decision refreshes the detail immediately, but at that moment
  // the photo is still in flight, so `landed` is still false and the tile still
  // reads "Menunggu". Nothing re-fetched afterwards, so the surveyor had to
  // close and reopen the area to see their own photo arrive. Tie the re-fetch
  // to the upload actually completing, not to the recording.
  const refreshIfUploaded = useCallback(
    (sent: number) => {
      if (sent <= 0) return;
      void refreshAreas();
      void refreshDetail(selected);
    },
    [refreshAreas, refreshDetail, selected],
  );

  useEffect(() => {
    if (!me) return;
    void refreshAreas();
    void refreshOutbox();
    // Subscribe rather than pass a callback into startAutoFlush: recording a
    // decision starts a flush of its own, and the badge has to move for that one
    // too, in both directions -- a failure has to appear now, and a successful
    // drain has to disappear now, not on the next 15s tick.
    const off = onFlushEvent((e) => {
      setUploading(e.uploading);
      setUploadError(e.lastError);
      void refreshOutbox();
      refreshIfUploaded(e.sent);
    });
    const stop = startAutoFlush();
    return () => {
      off();
      stop();
    };
  }, [me, refreshAreas, refreshOutbox, refreshIfUploaded]);

  // Warm the map chunk once signed in: peta paints only when the chunk is
  // in hand, so the fetch that opens the gate must not depend on how the
  // session started (boot restore and fresh login alike). Tile fetches are
  // immutable; a failed warm is silent by design (mount retries).
  useEffect(() => {
    if (!me || mapChunk) return;
    let cancelled = false;
    const warm = () =>
      void import("./pages/Map").then(async (m) => {
        if (cancelled) return;
        setMapChunk(true);
        try {
          const { PMTiles } = await import("pmtiles");
          const style = await (await fetch("/style-dark.json")).json();
          const archive = style.sources.openmaptiles.url.replace("pmtiles://", "");
          const p = new PMTiles(`${location.origin}${archive}`);
          const [lng, lat] = m.HOME;
          const z = 14;
          const cx = Math.floor(((lng + 180) / 360) * 2 ** z);
          const latR = (lat * Math.PI) / 180;
          const cy = Math.floor(
            (((1 - Math.log(Math.tan(latR) + 1 / Math.cos(latR)) / Math.PI) / 2) * 2 ** z),
          );
          await Promise.allSettled(
            [cx - 1, cx, cx + 1].flatMap((x) =>
              [cy - 1, cy, cy + 1].map((y) => p.getZxy(z, x, y)),
            ),
          );
        } catch {
          /* mount retries on demand */
        }
      });
    // A plain timeout, not requestIdleCallback: the gate below needs the
    // chunk promptly, and a busy renderer never goes idle (the suite caught
    // exactly this -- 15s without a single idle window).
    const t = setTimeout(warm, 50);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [me, mapChunk]);

  useEffect(() => {
    void refreshDetail(selected);
  }, [selected, refreshDetail]);

  // A long-lived toast is worse than a short one, but an error must not vanish
  // before it is read, so errors are sticky until the next action.
  useEffect(() => {
    if (!toast || toast.tone === "red") return;
    const t = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(t);
  }, [toast]);

  // Deferred paint: navigating to peta holds the previous screen until the
  // list and the map chunk are both in hand. Above the early returns -- a
  // hook below them renders conditionally and crashes (#310).
  const petaReady = areas !== null && mapChunk;
  useEffect(() => {
    if (view !== "peta" || petaReady) setShownView(view);
  }, [view, petaReady]);

  const say = useCallback((tone: "red" | "green" | "blue", text: string) => setToast({ tone, text }), []);

  const afterChange = useCallback(async () => {
    await Promise.all([refreshAreas(), refreshDetail(selected), refreshOutbox()]);
  }, [refreshAreas, refreshDetail, refreshOutbox, selected]);

  if (booting) {
    return (
      <div className="auth-wrap">
        <span className="spinner" />
      </div>
    );
  }

  if (!me) {
    return (
      <Login
        onDone={(m) => {
          setMe(m);
          setView("peta");
        }}
      />
    );
  }

  if (unlock) {
    return (
      <Login
        mode="pin"
        title={me.user.display_name}
        subtitle="Masukkan PIN untuk membuka"
        onDone={() => setUnlock(false)}
        onCancel={() => {
          void api.logout().finally(() => setMe(null));
        }}
      />
    );
  }

  const pendingCount = outbox.length;
  // One paint: list and map arrive together. Rendering the sidebar chrome
  // before the data lands is the staged pop the load frames caught.
  return (
    <div className="app">
      {shownView === "peta" && !petaReady && <ScreenLoading label="Memuat peta…" />}
      {shownView === "peta" &&
        (petaReady ? (
          <Areas
            areas={areas}
            selected={selected}
            onSelect={setSelected}
            onRefresh={refreshAreas}
            canCreate={can("area.create")}
            onDrawn={async (id) => {
              await refreshAreas();
              setSelected(id);
              setView("peta");
            }}
            me={me}
          />
        ) : (
          // Layout holder: keeps the 320px column from collapsing and
          // snapping when the list paints. Content arrives all at once.
          <aside className="sidebar" aria-hidden />
        ))}

      <div className="main">
        <header
          className="brand"
          style={{ borderBottom: "1px solid var(--border)", background: "var(--card)" }}
        >
          <div className="brand-mark" aria-hidden>
            GP
          </div>
          <div>
            <div className="brand-name">GEOpermitLIVE</div>
            <div className="brand-sub">
              {me.roles.map((r) => ROLE_LABEL[r] ?? r).join(" · ")} · {me.user.display_name}
            </div>
          </div>
          <div className="spacer" />

          {pendingCount > 0 && (
            <button
              className="btn btn-sm"
              onClick={() =>
                say(
                  uploadError ? "red" : "blue",
                  uploadError
                    ? `${pendingCount} bukti belum terkirim: ${uploadError}`
                    : `${pendingCount} bukti menunggu unggahan. Akan dikirim otomatis.`,
                )
              }
              title={uploadError ? `Gagal: ${uploadError}` : "Lihat antrean unggahan"}
            >
              <span className={`chip ${uploadError ? "chip-error" : "chip-pending"}`}>
                {uploading > 0 ? "⤒" : "⏳"} {pendingCount} menunggu
              </span>
            </button>
          )}

          <nav className="rowflex" style={{ marginLeft: 8 }}>
            <button
              className={`btn btn-sm ${view === "peta" ? "btn-primary" : ""}`}
              onClick={() => setView("peta")}
            >
              Peta
            </button>
            <button
              className={`btn btn-sm ${view === "dasbor" ? "btn-primary" : ""}`}
              onClick={() => setView("dasbor")}
            >
              Dashboard
            </button>
            <button
              className={`btn btn-sm ${view === "database" ? "btn-primary" : ""}`}
              onClick={() => setView("database")}
            >
              Database
            </button>
            {can("user.manage") && (
              <button
                className={`btn btn-sm ${view === "pengguna" ? "btn-primary" : ""}`}
                onClick={() => setView("pengguna")}
              >
                User
              </button>
            )}
          </nav>

          <button
            className="btn btn-sm btn-ghost"
            style={{ marginLeft: 8 }}
            onClick={() => {
              void api.logout().finally(() => {
                setMe(null);
                setSelected(null);
              });
            }}
          >
            Keluar
          </button>
        </header>

        {shownView === "peta" && (
          <Suspense fallback={<div className="map-wrap map-loading">Memuat peta…</div>}>
          <MapPage
            areas={areas ?? []}
            selected={selected}
            detail={detail}
            canCreate={can("area.create")}
            canEditGeometry={can("area.geometry.edit")}
            onSelect={setSelected}
            onChanged={afterChange}
            say={say}
          />
          </Suspense>
        )}
        {shownView === "dasbor" && <Dashboard onOpen={(id) => { setSelected(id); setView("peta"); }} canOpen={can("evidence.open")} canUpload={can("area.geometry.edit")} />}
        {shownView === "database" && <Database onOpen={(id) => { setSelected(id); setView("peta"); }} canOpen={can("evidence.open")} />}
        {shownView === "pengguna" && can("user.manage") && <Users say={say} />}
      </div>

      {shownView === "peta" && detail && (
        <Timeline
          detail={detail}
          canRecord={can("approval.record")}
          canSignOff={can("approval.internal_signoff")}
          canEditType={can("area.geometry.edit")}
          canEditPlot={can("area.geometry.edit")}
          canOpenEvidence={can("evidence.open")}
          canDeleteEvidence={can("evidence.delete")}
          onChanged={afterChange}
          say={say}
          onClose={() => setSelected(null)}
        />
      )}

      {toast && (
        <div
          className={`warn warn-${toast.tone === "green" ? "green" : toast.tone}`}
          style={{ position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)", zIndex: 60, maxWidth: "90vw" }}
          role="status"
        >
          {toast.text}
          <button className="btn btn-sm btn-ghost" onClick={() => setToast(null)}>
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

const ROLE_LABEL: Record<string, string> = {
  surveyor: "Surveyor",
  supervisor: "Supervisor",
  management: "Manajemen",
  admin: "Admin",
};
